(() => {
  'use strict';

  if (window.__ARTO_SHARED_CART_LIMITS__) return;
  window.__ARTO_SHARED_CART_LIMITS__ = true;

  const API =
    window.ARTOMATOR_PATCH_API_BASE ||
    'https://us-central1-a2-migration.cloudfunctions.net/api';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#]|$)/;
  const normalizeSku = value => String(value || '').trim().toUpperCase();
  const availableFor = item =>
    Math.max(0, Number(item?.onHand || 0) - Number(item?.committed || 0));

  let state = emptyState();
  let activeRefresh = null;
  let refreshAgain = false;
  let refreshTimer = null;
  let correcting = false;
  let noticeTimer = null;

  function emptyState() {
    return {
      cart: null,
      linesByKey: new Map(),
      quantityBySku: new Map(),
      limitBySku: new Map()
    };
  }

  function inventorySku(line) {
    const properties = line?.properties || {};
    return normalizeSku(
      properties._arto_inventory_sku ||
      properties['_Blank SKU'] ||
      properties['Blank SKU']
    );
  }

  async function jsonResponse(url, options) {
    const response = await fetch(url, options);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.message || 'Inventory is unavailable.');
    return body;
  }

  function makeState(cart, inventoryItems) {
    const next = emptyState();
    next.cart = cart;
    (inventoryItems || []).forEach(item => {
      next.limitBySku.set(normalizeSku(item.sku), availableFor(item));
    });
    (cart.items || []).forEach(line => {
      const sku = inventorySku(line);
      if (!sku) return;
      next.linesByKey.set(String(line.key), line);
      next.quantityBySku.set(
        sku,
        (next.quantityBySku.get(sku) || 0) + Math.max(0, Number(line.quantity) || 0)
      );
    });
    return next;
  }

  function overLimitEntries(snapshot) {
    const entries = [];
    snapshot.quantityBySku.forEach((quantity, sku) => {
      const limit = snapshot.limitBySku.get(sku);
      if (typeof limit === 'number' && quantity > limit) {
        entries.push({ sku, quantity, limit });
      }
    });
    return entries;
  }

  function hasUnknownInventory(snapshot) {
    return [...snapshot.quantityBySku.keys()].some(sku => !snapshot.limitBySku.has(sku));
  }

  function showNotice(message) {
    let notice = document.querySelector('[data-arto-cart-inventory-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.dataset.artoCartInventoryNotice = 'true';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      Object.assign(notice.style, {
        position: 'fixed',
        left: '50%',
        bottom: '24px',
        zIndex: '2147483647',
        maxWidth: 'calc(100vw - 32px)',
        padding: '12px 18px',
        borderRadius: '6px',
        background: '#222',
        color: '#fff',
        font: '600 15px/1.25 Arial, sans-serif',
        textAlign: 'center',
        boxShadow: '0 4px 18px rgba(0,0,0,.25)',
        transform: 'translateX(-50%)'
      });
      document.body.appendChild(notice);
    }
    notice.textContent = message;
    notice.hidden = false;
    window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(() => { notice.hidden = true; }, 3500);
  }

  function isIncreaseButton(button) {
    const label = String(button?.getAttribute('aria-label') || '').toLowerCase();
    if (/increase|increment|plus|add one/.test(label)) return true;
    const selector = button?.closest('.quantity-selector');
    const buttons = selector ? [...selector.querySelectorAll('button')] : [];
    return buttons.length > 1 && buttons[buttons.length - 1] === button;
  }

  function decorateDrawer() {
    const drawer = document.getElementById('slidecarthq');
    if (!drawer) return;

    drawer.querySelectorAll('[data-line-item-key]').forEach(row => {
      const line = state.linesByKey.get(String(row.getAttribute('data-line-item-key')));
      const sku = inventorySku(line);
      const limit = state.limitBySku.get(sku);
      const quantity = state.quantityBySku.get(sku) || 0;
      const atLimit = Boolean(sku) && typeof limit === 'number' && quantity >= limit;
      const input = row.querySelector('.quantity-selector input');

      row.dataset.artoInventoryManaged = sku ? 'true' : 'false';
      row.dataset.artoInventoryAtLimit = atLimit ? 'true' : 'false';
      if (input && line && typeof limit === 'number') {
        const otherLinesQuantity = quantity - Number(line.quantity || 0);
        input.max = String(Math.max(0, limit - otherLinesQuantity));
      }
      row.querySelectorAll('.quantity-selector button').forEach(button => {
        if (!isIncreaseButton(button)) return;
        if (atLimit) {
          button.dataset.artoInventoryDisabled = 'true';
          button.setAttribute('aria-disabled', 'true');
          button.title = `Only ${limit} available`;
          button.style.setProperty('opacity', '0.4', 'important');
          button.style.setProperty('cursor', 'not-allowed', 'important');
        } else if (button.dataset.artoInventoryDisabled === 'true') {
          delete button.dataset.artoInventoryDisabled;
          button.removeAttribute('aria-disabled');
          button.removeAttribute('title');
          button.style.removeProperty('opacity');
          button.style.removeProperty('cursor');
        }
      });
    });
  }

  async function clampCart(snapshot) {
    const oversold = overLimitEntries(snapshot);
    if (!oversold.length) return { adjusted: false, cart: snapshot.cart };

    correcting = true;
    try {
      for (const entry of oversold) {
        let excess = entry.quantity - entry.limit;
        const lines = (snapshot.cart.items || []).filter(line => inventorySku(line) === entry.sku);
        for (const line of lines) {
          if (excess <= 0) break;
          const current = Math.max(0, Number(line.quantity) || 0);
          const remove = Math.min(current, excess);
          excess -= remove;
          await jsonResponse('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: current - remove })
          });
        }
      }
    } finally {
      correcting = false;
    }

    const cart = await jsonResponse('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } });
    return { adjusted: true, cart };
  }

  async function refreshNow({ clamp = true } = {}) {
    const cart = await jsonResponse('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } });
    const skus = [...new Set((cart.items || []).map(inventorySku).filter(Boolean))];
    if (!skus.length) {
      state = makeState(cart, []);
      decorateDrawer();
      return { valid: true, adjusted: false, state };
    }

    const inventory = await jsonResponse(
      `${API}/patch-programs/blanks/inventory?skus=${encodeURIComponent(skus.join(','))}`,
      { cache: 'no-store', headers: { Accept: 'application/json' } }
    );
    state = makeState(cart, inventory.items || []);

    let adjusted = false;
    if (clamp && overLimitEntries(state).length) {
      const result = await clampCart(state);
      adjusted = result.adjusted;
      if (adjusted) {
        state = makeState(result.cart, inventory.items || []);
        showNotice('Quantity adjusted to the available inventory.');
        if (typeof window.SLIDECART_UPDATE === 'function') {
          try { window.SLIDECART_UPDATE(() => {}); } catch (error) {}
        }
      }
    }

    decorateDrawer();
    const valid = !hasUnknownInventory(state) && !overLimitEntries(state).length;
    document.dispatchEvent(new CustomEvent('arto:cart-inventory', {
      detail: { valid, adjusted }
    }));
    return { valid, adjusted, state };
  }

  function refresh(options) {
    if (activeRefresh) {
      refreshAgain = true;
      return activeRefresh;
    }
    activeRefresh = refreshNow(options)
      .finally(() => {
        activeRefresh = null;
        if (refreshAgain) {
          refreshAgain = false;
          scheduleRefresh();
        }
      });
    return activeRefresh;
  }

  function scheduleRefresh() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      refresh().catch(error => console.warn('[inventory] cart check failed:', error));
    }, 100);
  }

  // Read-only storefront usage for product selectors. This is not a global
  // reservation: it only describes the current Shopify cart in this browser.
  window.__ARTO_SHARED_CART_QUANTITY__ = sku =>
    state.quantityBySku.get(normalizeSku(sku)) || 0;

  window.__ARTO_ENSURE_CART_INVENTORY__ = async () => {
    let result;
    try {
      result = await refresh({ clamp: true });
    } catch (cause) {
      const error = new Error('We could not verify inventory. Please wait a moment and try again.');
      error.code = 'ARTO_INVENTORY_LIMIT';
      error.cause = cause;
      throw error;
    }
    if (result.adjusted || !result.valid) {
      const error = new Error('We adjusted your cart to the available inventory. Please review it before checkout.');
      error.code = 'ARTO_INVENTORY_LIMIT';
      throw error;
    }
    return result;
  };

  document.addEventListener('click', event => {
    const button = event.target.closest?.('#slidecarthq .quantity-selector button');
    if (!button || !isIncreaseButton(button)) return;
    const row = button.closest('[data-line-item-key]');
    const line = state.linesByKey.get(String(row?.getAttribute('data-line-item-key') || ''));
    const sku = inventorySku(line);
    const limit = state.limitBySku.get(sku);
    const quantity = state.quantityBySku.get(sku) || 0;
    if (!sku || typeof limit !== 'number' || quantity < limit) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showNotice(`Only ${limit} available for this item.`);
  }, true);

  document.addEventListener('change', event => {
    const input = event.target.closest?.('#slidecarthq [data-line-item-key] .quantity-selector input');
    if (!input) return;
    const row = input.closest('[data-line-item-key]');
    const line = state.linesByKey.get(String(row?.getAttribute('data-line-item-key') || ''));
    const sku = inventorySku(line);
    const limit = state.limitBySku.get(sku);
    if (!sku || typeof limit !== 'number' || !line) return;
    const requested = Math.max(0, Number(input.value) || 0);
    const otherLines = (state.quantityBySku.get(sku) || 0) - Number(line.quantity || 0);
    if (otherLines + requested <= limit) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    input.value = String(line.quantity);
    showNotice(`Only ${limit} available for this item.`);
  }, true);

  const previousFetch = window.fetch;
  window.fetch = function (input) {
    const url = typeof input === 'string' ? input : String(input?.url || '');
    const cartMutation = CART_MUTATION.test(url);
    const request = previousFetch.apply(this, arguments);
    if (cartMutation && !correcting) request.then(scheduleRefresh, scheduleRefresh);
    return request;
  };

  const previousOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__artoCartMutation = Boolean(url && CART_MUTATION.test(String(url)));
    return previousOpen.apply(this, arguments);
  };
  const previousSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__artoCartMutation && !correcting) {
      this.addEventListener('loadend', scheduleRefresh, { once: true });
    }
    return previousSend.apply(this, arguments);
  };

  new MutationObserver(decorateDrawer).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('bb:state', scheduleRefresh);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRefresh, { once: true });
  } else {
    scheduleRefresh();
  }
})();
